import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    HolderOutlined,
    MoreOutlined,
    PlusOutlined,
    RetweetOutlined,
} from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Dropdown, Flex, MenuProps } from "antd";
import { FC, memo, ReactNode, useMemo } from "react";
import { Point, Schedule } from "tm-shared/types";

import { PointInfo } from "@/domains/point";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";

import { StyledScheduleCard } from "./Styled";

export type ScheduleMenuAction =
    | "view"
    | "edit"
    | "move"
    | "delete"
    | "addUnplanned";

export type ScheduleMenuConfig = {
    type: ScheduleMenuAction;
    label: string;
    disabled?: (schedule: Schedule) => boolean;
    onClick: (schedule: Schedule) => void;
};

interface ScheduleCardProps {
    schedule: Schedule;
    point?: Point;
    isSelected?: boolean;
    hideMenu?: boolean;
    disableDrag?: boolean;
    onClick?: (item: Schedule) => void;
    onTouchStart?: () => void;
    scheduleMenuConfig: ScheduleMenuConfig[];
}

const IconsMap: Record<ScheduleMenuAction, ReactNode> = {
    view: <EyeOutlined />,
    edit: <EditOutlined />,
    move: <RetweetOutlined />,
    delete: <DeleteOutlined />,
    addUnplanned: <PlusOutlined />,
};

export const ScheduleCard: FC<ScheduleCardProps> = memo(
    ({
        schedule,
        point,
        isSelected = false,
        hideMenu = false,
        disableDrag = false,
        onClick = () => {},
        onTouchStart = () => {},
        scheduleMenuConfig,
    }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
        } = useSortable({
            id: schedule.pointId,
            disabled: disableDrag,
        });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : undefined,
        };

        const menuItems: MenuProps["items"] = useMemo(() => {
            return scheduleMenuConfig.map(({ type, disabled, label }) => ({
                key: type,
                label,
                disabled: disabled?.(schedule),
                icon: IconsMap[type],
                danger: type === "delete",
            }));
        }, [schedule, scheduleMenuConfig]);

        const actionsMap = scheduleMenuConfig.reduce<
            Partial<Record<ScheduleMenuAction, (schedule: Schedule) => void>>
        >((acc, { type, onClick }) => ({ ...acc, [type]: onClick }), {});

        const handleMenuClick: MenuProps["onClick"] = (event) => {
            actionsMap[event.key as ScheduleMenuAction]?.(schedule);
        };

        return (
            <div ref={setNodeRef} style={style}>
                <StyledScheduleCard
                    $hasDebt={point?.hasDebt}
                    $selected={isSelected}
                    onTouchStart={onTouchStart}
                    onClick={() => onClick(schedule)}
                >
                    {!disableDrag && (
                        <Button
                            style={{ width: 16, marginRight: 8 }}
                            icon={<HolderOutlined />}
                            {...listeners}
                            {...attributes}
                            type="text"
                        />
                    )}

                    <Flex
                        vertical
                        align="flex-start"
                        style={{ marginRight: "auto" }}
                    >
                        {point && <PointInfo point={point} />}

                        <Flex gap={4}>
                            <SecondaryText>Разів на тиждень:</SecondaryText>
                            <PrimaryText>
                                {schedule?.days?.length || 0}
                            </PrimaryText>
                        </Flex>
                    </Flex>

                    {!hideMenu && (
                        <Dropdown
                            trigger={["click"]}
                            menu={{
                                items: menuItems,
                                onClick: (e) => handleMenuClick(e),
                            }}
                        >
                            <Button
                                icon={<MoreOutlined />}
                                type="text"
                                style={{ width: 24 }}
                            />
                        </Dropdown>
                    )}
                </StyledScheduleCard>
            </div>
        );
    },
);

ScheduleCard.displayName = "ScheduleCard";
